/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import * as fromAccessActions from 'app/shared/store/actions/access.action';
import { UserService } from 'app/shared/services/user.service';
import { Constants } from '@shared/models/constants.model';

@Injectable()
export class AccessEffects {
  getInviteesPurchases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAccessActions.LOAD_USER_ACCESS),
      switchMap((action: fromAccessActions.LoadUserAccess) => {
        return this.userService.getUserAccess(action.payload.userId, action.payload.userType).pipe(
          map(this.parseProducts),
          map((response) => {
            const athletes = this.extractAthletes(response, action.payload.userId);
            const parents = this.extractParents(response, action.payload.userId);
            const products = this.filterAddons(response, athletes, parents);

            return new fromAccessActions.LoadUserAthletesSuccess({
              athletes,
              parents,
              products,
            });
          }),
          catchError((error) => {
            console.error(error);
            return of(new fromAccessActions.LoadUserAthletesFail());
          })
        );
      })
    )
  );

  constructor(private actions$: Actions, private userService: UserService) {}

  private extractAthletes(purchases: any[], currentUserId: number) {
    const allInvites = purchases.reduce((acc, p) => {
      if (p?.invites?.length) {
        p.invites
          .filter((i) => (!i.isTeamInvite || (i.isTeamInvite && i.isTPAthlete)) && i.userId === currentUserId)
          .forEach((invite) => {
            if (!invite.id || invite.toUser?.id === currentUserId) {
              return;
            }
            const exists = acc.find((a) => a.id === invite.toUser?.id);
            if (!exists) {
              acc.push({
                ...invite.toUser,
                avatar: invite.toUser?.avatar || Constants.DEFAULT_AVATAR_ICON_SVG,
                avatarThumb: invite.toUser?.avatarThumb || Constants.DEFAULT_AVATAR_ICON_SVG,
              });
            }
          });
      }
      return acc;
    }, []);
    return allInvites;
  }

  private extractParents(purchases: any[], currentUserId: number) {
    // create a set with all parents
    const allParents = purchases.reduce((acc, purchase) => {
      if (purchase.userId === currentUserId) {
        return acc;
      }

      if (purchase.type !== Constants.PURCHASE_TYPE.ONE_EVENT_TP) {
        const inviteForUser = purchase.invites.find((invite) => invite.toUser?.id === currentUserId);

        if (inviteForUser && currentUserId !== inviteForUser.userId) {
          acc.add(purchase.userId);
        }

        // acc.add(purchase.userId);
        return acc;
      }

      // if team package purchase only set parent if the invite was sent
      // by a different person than the one who created the purchase then
      // the person who sent the invite is the parent
      if (purchase.invites?.length) {
        const inviteForUser = purchase.invites.find((invite) => invite.toUser?.id === currentUserId);

        if (inviteForUser && purchase.userId !== inviteForUser.userId) {
          acc.add(inviteForUser.userId);
        }
      }
      return acc;
    }, new Set());

    // convert to array of objects
    return [...allParents].map((parentId) => ({ id: parentId }));
  }

  private parseProducts(products) {
    return products.map((product) => ({
      ...product,
      event: { ...product.event, price: product?.event?.price ? JSON.parse(product?.event?.price) : null },
      metadata: JSON.parse(product.metadata),
    }));
  }

  private filterAddons(products, myAthletes, myParents) {
    return products.map((product) => ({
      ...product,
      // filter out addons that are not purchased by this user's athletes, this user's parents or this specific user
      // this is to filter out breakdowns bought by an athlete invited to a TP
      addons:
        product.addons?.filter((addon) => {
          return (
            addon.userId === product.userId ||
            myAthletes.some((athlete) => athlete.id === addon.userId) ||
            myParents.some((parent) => parent.id === addon.userId)
          );
        }) || [],
    }));
  }
}
